import { CartAddEvent, ThemeEvents } from '@theme/events';

const TEST_ID = 'MC_PDP_SETUPGRADE_BIYU';
const DEAL_ID = '902c20ae-00fb-4863-8baa-ff87c22da559';
const numericId = (value) => String(value ?? '').split('/').pop();

/** An additive offer. Hidden/control instances never intercept a purchase. */
class MaisonSetUpgrade extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.closest('form');
    this.toggle = this.querySelector('[data-set-toggle]');
    this.config = JSON.parse(this.querySelector('[data-set-config]').textContent);
    this.status = this.querySelector('[data-set-status]');
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency', currency: this.dataset.currency || 'EUR',
    });
    this.section = this.closest('.shopify-section');
    this.variantId = this.dataset.mainVariant;
    this.busy = false;
    this.variantPending = false;

    // Only an unpublished theme can opt into a URL-based review; no live bypass.
    if (this.dataset.previewAllowed === 'true' &&
      new URLSearchParams(location.search).get('maison-preview') === TEST_ID) {
      this.style.display = 'block';
    }
    this.toggle.addEventListener('change', () => {
      this.status.hidden = true;
      this.track(this.toggle.checked ? 'selected' : 'deselected');
      this.render();
    }, { signal });
    this.form?.addEventListener('change', () => this.render(), { signal });
    this.form?.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.render(), { signal });
    this.section?.addEventListener(ThemeEvents.variantSelected, () => {
      this.variantPending = true;
    }, { signal });
    this.section?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      if (String(event.detail.data.productId) !== this.dataset.productId || event.detail.data.newProduct) {
        this.toggle.checked = false;
        this.toggle.disabled = true;
        return;
      }
      this.variantPending = false;
      this.variantId = String(event.detail.resource?.id || '');
      this.render();
    }, { signal });

    // Capture the selected offer before app/theme click handlers can submit a second request.
    window.addEventListener('click', (event) => {
      const button = event.target.closest?.('button[type="submit"], input[type="submit"]');
      if (!button || button.form !== this.form || (!this.busy && !this.isSelected())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.form.reportValidity()) this.addSet();
    }, { capture: true, signal });
    window.addEventListener('submit', (event) => {
      if (event.target !== this.form || (!this.busy && !this.isSelected())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addSet();
    }, { capture: true, signal });

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && this.isVisible()) {
        this.track('impression');
        this.observer.disconnect();
      }
    });
    this.observer.observe(this);
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  isVisible() {
    return this.getClientRects().length > 0 && getComputedStyle(this).visibility !== 'hidden';
  }

  isSelected() { return this.toggle?.checked && (!this.toggle.disabled || this.busy) && this.isVisible(); }

  selection() {
    const data = new FormData(this.form);
    const id = String(data.get('id') || this.variantId);
    const variant = this.config.variants.find((item) => String(item.id) === id);
    const quantity = Number(data.get('quantity') || 1);
    return { data, variant, quantity };
  }

  offer() {
    const deal = this.config.kaching;
    const mainId = this.dataset.productId;
    const companionId = this.dataset.companionProduct;
    if (!deal || deal.id !== DEAL_ID || deal.blockVisibility !== 'selected-products' ||
      !deal.selectedProductIds?.some((id) => numericId(id) === mainId) ||
      deal.excludedProductIds?.some((id) => numericId(id) === mainId) ||
      (deal.marketId && numericId(deal.marketId) !== this.dataset.marketId) ||
      deal.excludedMarketIds?.some((id) => numericId(id) === this.dataset.marketId) ||
      (deal.excludeB2bCustomers && this.dataset.b2b === 'true') ||
      deal.priceRounding || deal.useProductCompareAtPrice || deal.abTestVariantId ||
      ['subscriptionsEnabled', 'progressiveGiftsEnabled', 'checkboxUpsellsEnabled',
        'collectionBreaksEnabled', 'scratchOffEnabled'].some((key) => deal[key])) return null;
    const bar = deal.dealBars?.find((bar) => bar.dealBarType === 'bundle' &&
      !bar.sellingPlanEnabled && !bar.showAsSoldOutEnabled &&
      !bar.freeGifts?.length && !bar.upsells?.length &&
      bar.bundleProducts?.length === 2 && bar.bundleProducts.every((item) =>
        [mainId, companionId].includes(numericId(item.productGID)) &&
        Number(item.quantity) === 1 && item.discountType === 'percentage' && Number(item.discountValue) === 10) &&
      new Set(bar.bundleProducts.map((item) => numericId(item.productGID))).size === 2);
    return bar ? { deal, bar } : null;
  }

  setTotal(variant, quantity) {
    // Kaching rounds the discounted unit price upward, then multiplies quantity.
    return (Math.ceil(variant.price * 90 / 100) +
      Math.ceil(Number(this.dataset.companionPrice) * 90 / 100)) * quantity;
  }

  render() {
    const { variant, quantity } = this.selection();
    const valid = variant?.available && Number.isSafeInteger(quantity) && quantity > 0;
    this.toggle.disabled = !valid || this.busy;
    if (!valid) {
      this.toggle.checked = false;
      return;
    }
    const normal = (variant.price + Number(this.dataset.companionPrice)) * quantity;
    const total = this.setTotal(variant, quantity);
    this.querySelector('[data-set-normal]').textContent = this.money.format(normal / 100);
    this.querySelector('[data-set-price]').textContent = this.money.format(total / 100);
  }

  message(text) {
    this.status.textContent = text;
    this.status.hidden = false;
  }

  track(action) {
    if (!this.isVisible()) return;
    if (action === 'added') {
      try {
        window.Shopify?.analytics?.publish('eab:custom_event', {
          tracking_id: '59d8', event_target: 'biyu_set_added_verified_discount',
        });
      } catch (error) { console.error('[Maison set event]', error); }
    }
    this.dispatchEvent(new CustomEvent('maison:set-upgrade', { bubbles: true, detail: {
      testId: TEST_ID, action, productId: this.dataset.productId,
      companionVariantId: this.dataset.companionVariant,
    } }));
  }

  async request(path, body) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' } });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart request failed');
    return result;
  }

  async addSet() {
    if (this.busy || !this.isSelected()) return;
    const offer = this.offer();
    if (this.dataset.discountReady !== 'true' || !offer) {
      this.message(this.config.strings.preview);
      return;
    }
    const { data, variant, quantity } = this.selection();
    if (this.variantPending || !variant?.available || !Number.isSafeInteger(quantity) || quantity < 1 || data.get('selling_plan')) {
      this.message(this.config.strings.unavailable);
      return;
    }
    const pair = `${TEST_ID}:${crypto.randomUUID()}`;
    const chosen = offer.bar.bundleProducts.map((entry) => ({
      entry, variantId: numericId(entry.productGID) === this.dataset.productId
        ? variant.id : Number(this.dataset.companionVariant),
    }));
    if (chosen.some(({ entry, variantId }) => entry.variantGIDs?.length &&
      !entry.variantGIDs.some((id) => numericId(id) === String(variantId)))) {
      this.message(this.config.strings.unavailable);
      return;
    }
    // Same grouping algorithm and fixed-product metadata as Kaching's bundle adapter.
    // Kaching treats the first fixed bundle product as the main line, even when
    // it differs from the product page. The remaining products are bundle lines.
    const groupInput = JSON.stringify([[{ variantId: chosen[0].variantId, quantity }], chosen.slice(1).map(({ entry, variantId }) =>
      ({ id: entry.id, variantId, quantity })), [], [], [], [], [], null, null, offer.bar.id]);
    let hash = 2166136261;
    for (let i = 0; i < groupInput.length; i++) hash = Math.imul(hash ^ groupInput.charCodeAt(i), 16777619) >>> 0;
    let group = '';
    for (let i = 0; i < 4; i++) {
      group = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'[hash % 62] + group;
      hash = Math.floor(hash / 62);
    }
    const kachingProperty = (variantId) => JSON.stringify({
      id: group, deal: offer.deal.nanoId || offer.deal.id, bar: offer.bar.id,
      ...(offer.deal.versionId ? { v: offer.deal.versionId } : {}),
      ...(chosen[0].variantId === Number(variantId)
        ? { pp: Number(this.dataset.productId), main: true,
            ...(offer.deal.marketId ? { market: offer.deal.marketId } : {}) }
        : { bundleProduct: chosen.find((item) => item.variantId === Number(variantId)).entry.id }),
    });
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const items = [
      { id: variant.id, quantity, properties: { ...properties, __kaching_bundles: kachingProperty(variant.id), _maison_set: pair, _maison_test: TEST_ID } },
      { id: Number(this.dataset.companionVariant), quantity, properties: { __kaching_bundles: kachingProperty(this.dataset.companionVariant), _maison_set: pair, _maison_test: TEST_ID } },
    ];
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.message(this.config.strings.loading);
    const buttons = [...this.form.querySelectorAll('button[type="submit"]')];
    const disabled = buttons.map((button) => button.disabled);
    buttons.forEach((button) => { button.disabled = true; });
    // Lock selection while adding; busy events remain intercepted even if selection changes.
    this.toggle.disabled = true;
    let previousCodes = [];
    try {
      const before = await this.request('cart.js');
      previousCodes = (before.discount_codes || []).map((discount) => discount.code);
      await this.request('cart/add.js', { items });
      const code = this.dataset.discountCode.trim();
      const codes = code ? [...new Set([...previousCodes, code])] : previousCodes;
      const cart = await this.request('cart/update.js', {
        ...(code ? { discount: codes.join(',') } : {}), sections, sections_url: location.pathname,
      });
      const added = cart.items.filter((item) => item.properties?._maison_set === pair);
      const expected = this.setTotal(variant, quantity);
      const actual = added.reduce((sum, item) => sum + item.final_line_price, 0);
      const complete = added.length === 2 && items.every((item) => added.some((line) =>
        line.variant_id === item.id && line.quantity === quantity));
      if (this.dataset.previewAllowed === 'true') {
        this.dataset.verification = JSON.stringify({ expected, actual, complete,
          lines: cart.items.map(({ variant_id, quantity, original_line_price, final_line_price, properties, line_level_discount_allocations }) =>
            ({ variant_id, quantity, original_line_price, final_line_price, properties, line_level_discount_allocations })) });
      }
      if (!complete || actual > expected) {
        // Remove only the lines created by this attempt; preserve the pre-existing cart.
        const restored = await this.request('cart/update.js', {
          updates: Object.fromEntries(added.map((item) => [item.key, 0])),
          ...(code ? { discount: previousCodes.join(',') } : {}), sections, sections_url: location.pathname,
        });
        this.notifyCart(restored, true);
        this.message(this.config.strings.discountError);
        this.track('discount_error');
        return;
      }
      this.notifyCart(cart, false, quantity * 2);
      this.message(this.config.strings.success);
      this.track('added');
      this.toggle.checked = false;
    } catch (error) {
      // A lost response may follow a successful write. Never silently retry the purchase.
      this.message(this.config.strings.error);
      this.track('error');
      try { this.notifyCart(await this.request('cart.js'), true); } catch { /* Keep the recovery message. */ }
      console.error('[Maison set upgrade]', error);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      buttons.forEach((button, index) => { button.disabled = disabled[index]; });
      this.render();
    }
  }

  notifyCart(cart, didError, itemCount = 0) {
    this.dispatchEvent(new CartAddEvent(cart, this.dataset.mainVariant, {
      source: 'product-form-component', productId: this.dataset.productId,
      didError, itemCount, sections: cart.sections,
    }));
  }
}

if (!customElements.get('maison-set-upgrade')) customElements.define('maison-set-upgrade', MaisonSetUpgrade);
